import { useId } from 'react';
import { ArrowPathIcon, ArrowsUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { AssetSort, SortDir } from '@xphoto/shared';
import { rovingTabIndex, useRovingRadioGroup } from '../components/rovingFocus';
import { usePopoverClamp } from '../components/usePopoverClamp';
import { usePopoverDismiss } from '../components/usePopoverDismiss';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import type { MediaTypeFilter } from './state';

/**
 * Menu FILTRER (façon Windows) : un SEUL popover regroupant, en sections étiquetées,
 * le critère de tri (« Trier par »), l'ordre (« Ordre » croissant/décroissant) et le
 * filtre TYPE (« Type » tous/images/images animées/vidéos/vidéos avec son).
 *
 * Popover ÉTIQUETÉ (PAS `role="menu"` — il contient plusieurs `radiogroup`, ce que
 * `menu` n'admet pas ; même patron que `AccountSwitcher`). Chaque section est un
 * `radiogroup` de radios natives : l'option active est marquée par `aria-checked`
 * + une coche (jamais la couleur seule). Échap / clic-extérieur ferment et rendent
 * le focus au déclencheur.
 *
 * Clavier des trois `radiogroup` : flèches + `tabIndex` mobile (`rovingFocus.ts`) en
 * ACTIVATION MANUELLE. Ces trois choix pilotent la REQUÊTE de la galerie (tri, ordre,
 * type) : chacun réécrit l'URL et relance le chargement. Faire suivre la sélection au
 * focus lancerait une requête PAR FLÈCHE — même raisonnement que les onglets du lot 8.
 * Entrée/Espace appliquent le choix focalisé.
 */

/** Tous les critères de tri exposés → clé i18n du libellé (ordre d'affichage). */
const SORT_KEYS: Record<AssetSort, MessageKey> = {
  random: 'sort.random',
  takenAt: 'sort.takenAt',
  createdAt: 'sort.createdAt',
  filename: 'sort.filename',
  sizeBytes: 'sort.sizeBytes',
  viewCount: 'sort.viewCount',
  rating: 'sort.rating',
};

/** Ordre d'affichage stable des critères (« Aléatoire » en tête = défaut de l'Accueil). */
const SORT_ORDER: readonly AssetSort[] = [
  'random',
  'takenAt',
  'createdAt',
  'filename',
  'sizeBytes',
  'viewCount',
  'rating',
];

/** Sens de tri → clé i18n. */
const DIR_KEYS: Record<SortDir, MessageKey> = {
  asc: 'sort.asc',
  desc: 'sort.desc',
};
const DIR_ORDER: readonly SortDir[] = ['asc', 'desc'];

/** Valeur du filtre Type dans l'UI : `null` = « Tous » (pas de filtre). */
type MediaTypeChoice = MediaTypeFilter | null;
const MEDIA_TYPE_ORDER: readonly MediaTypeChoice[] = [
  null,
  'image',
  'animatedImage',
  'video',
  'videoWithSound',
];
const MEDIA_TYPE_KEYS: Record<'all' | MediaTypeFilter, MessageKey> = {
  all: 'sort.typeAll',
  image: 'sort.typeImage',
  animatedImage: 'sort.typeAnimatedImage',
  video: 'sort.typeVideo',
  videoWithSound: 'sort.typeVideoWithSound',
};

interface SortMenuProps {
  sort: AssetSort;
  onSortChange: (value: AssetSort) => void;
  dir: SortDir;
  onDirChange: (value: SortDir) => void;
  /**
   * Génère une NOUVELLE graine d'aléatoire (bouton « Re-mélanger ») → nouvel ordre.
   * Affiché uniquement quand le tri actif est « Aléatoire ».
   */
  onReshuffle: () => void;
  /** Un « Re-mélanger » est en vol : bouton désactivé (garde de double-soumission). */
  reshuffleBusy: boolean;
  /** Filtre Type courant (`null` = tous). */
  mediaType: MediaTypeChoice;
  onMediaTypeChange: (value: MediaTypeChoice) => void;
  /**
   * Compteurs par type (facultatifs) affichés à côté d'Images/Images animées/Vidéos/
   * Vidéos avec son.
   */
  imageCount?: number | undefined;
  animatedImageCount?: number | undefined;
  videoCount?: number | undefined;
  /** Nombre de vidéos avec piste audio (compteur `withAudio`). */
  videoWithSoundCount?: number | undefined;
  /** Bascule « Non taguées » active (filtre `tagged=false` du système « Taguer »). */
  untaggedOnly: boolean;
  /** Active/désactive le filtre « Non taguées ». */
  onUntaggedOnlyChange: (value: boolean) => void;
  /** Bascule « À valider (IA) » active (propositions IA en attente, non validées). */
  aiReviewOnly: boolean;
  /** Active/désactive le filtre « À valider (IA) ». */
  onAiReviewOnlyChange: (value: boolean) => void;
  /**
   * Coupe-circuit IA global (Incrément B). Quand `false`, le filtre « À valider (IA) »
   * est MASQUÉ (il n'a aucun sens sans IA) ; « Non taguées » reste, non lié à l'IA.
   */
  aiEnabled: boolean;
}

/** Une ligne d'option radio (critère / ordre / type), avec coche sur l'actif. */
function RadioRow({
  checked,
  onSelect,
  label,
  count,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
  count?: number | undefined;
}): React.JSX.Element {
  const { locale } = useI18n();
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      tabIndex={rovingTabIndex(checked)}
      onClick={onSelect}
      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand aria-checked:font-semibold aria-checked:text-brand motion-reduce:transition-none"
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="shrink-0 tabular-nums text-xs text-muted">
          {count.toLocaleString(locale)}
        </span>
      )}
      {checked ? (
        <CheckIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
      ) : (
        <span className="h-4 w-4 shrink-0" aria-hidden="true" />
      )}
    </button>
  );
}

export function SortMenu({
  sort,
  onSortChange,
  dir,
  onDirChange,
  onReshuffle,
  reshuffleBusy,
  mediaType,
  onMediaTypeChange,
  imageCount,
  animatedImageCount,
  videoCount,
  videoWithSoundCount,
  untaggedOnly,
  onUntaggedOnlyChange,
  aiReviewOnly,
  onAiReviewOnlyChange,
  aiEnabled,
}: SortMenuProps): React.JSX.Element {
  const { t } = useI18n();
  // Ouverture + fermeture (clic extérieur / Échap avec retour du focus) : noyau commun
  // à tous les popovers ancrés du projet (`components/usePopoverDismiss.ts`).
  const { open, rootRef, triggerRef, toggle } = usePopoverDismiss();
  // Maintien dans le cadre visible : ce déclencheur-ci est le cas d'école du défaut —
  // groupé à gauche de la barre, il poussait le panneau à −88 px sur 360 px de large.
  const panelRef = usePopoverClamp(open);
  const sortByLabelId = useId();
  const orderLabelId = useId();
  const typeLabelId = useId();
  const filterLabelId = useId();
  // Un seul gestionnaire pour les trois sections : chacune est un conteneur distinct,
  // les flèches ne franchissent donc jamais la frontière d'une section.
  const onRadiosKeyDown = useRovingRadioGroup('manual');

  // Libellé du déclencheur : reflète le critère courant (« Filtrer — Date de prise… »).
  const triggerLabel = `${t('sort.label')} — ${t(SORT_KEYS[sort])}`;

  const mediaTypeCount = (choice: MediaTypeChoice): number | undefined =>
    choice === 'image'
      ? imageCount
      : choice === 'animatedImage'
        ? animatedImageCount
        : choice === 'video'
          ? videoCount
          : choice === 'videoWithSound'
            ? videoWithSoundCount
            : undefined;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('sort.open')}
        title={triggerLabel}
        onClick={toggle}
        className={`flex h-11 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none ${
          open ? 'bg-surface-2 text-fg' : 'text-muted hover:bg-surface-2 hover:text-fg'
        }`}
      >
        <ArrowsUpDownIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline">{t('sort.label')}</span>
      </button>
      {open && (
        <div
          ref={panelRef}
          aria-label={t('sort.open')}
          className="absolute end-0 z-20 mt-1 min-w-56 rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {/* Section « Trier par » */}
          <p id={sortByLabelId} className="px-3 py-1.5 text-xs font-medium text-muted">
            {t('sort.sortBy')}
          </p>
          <div role="radiogroup" aria-labelledby={sortByLabelId} onKeyDown={onRadiosKeyDown}>
            {SORT_ORDER.map((value) => (
              <RadioRow
                key={value}
                checked={value === sort}
                onSelect={() => onSortChange(value)}
                label={t(SORT_KEYS[value])}
              />
            ))}
          </div>

          {/* « Re-mélanger » : uniquement en aléatoire → nouvelle graine, nouvel ordre. */}
          {sort === 'random' && (
            <button
              type="button"
              onClick={onReshuffle}
              disabled={reshuffleBusy}
              className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm font-medium text-brand transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand disabled:opacity-50 motion-reduce:transition-none"
            >
              <ArrowPathIcon
                className={`h-4 w-4 shrink-0 ${reshuffleBusy ? 'motion-safe:animate-spin' : ''}`}
                aria-hidden="true"
              />
              <span>{reshuffleBusy ? t('sort.reshuffling') : t('sort.reshuffle')}</span>
            </button>
          )}

          <div className="my-1 border-t border-border" />

          {/* Section « Ordre » (remplace l'ancien bouton bascule de sens). MASQUÉE en
              aléatoire : le sens croissant/décroissant n'a pas de sens pour un ordre
              pseudo-uniforme (le tri est piloté par la graine, pas par un sens). */}
          {sort !== 'random' && (
            <>
              <p id={orderLabelId} className="px-3 py-1.5 text-xs font-medium text-muted">
                {t('sort.order')}
              </p>
              <div role="radiogroup" aria-labelledby={orderLabelId} onKeyDown={onRadiosKeyDown}>
                {DIR_ORDER.map((value) => (
                  <RadioRow
                    key={value}
                    checked={value === dir}
                    onSelect={() => onDirChange(value)}
                    label={t(DIR_KEYS[value])}
                  />
                ))}
              </div>

              <div className="my-1 border-t border-border" />
            </>
          )}

          {/* Section « Type » (filtre par type ; « Tous » = pas de filtre) */}
          <p id={typeLabelId} className="px-3 py-1.5 text-xs font-medium text-muted">
            {t('sort.type')}
          </p>
          <div role="radiogroup" aria-labelledby={typeLabelId} onKeyDown={onRadiosKeyDown}>
            {MEDIA_TYPE_ORDER.map((choice) => (
              <RadioRow
                key={choice ?? 'all'}
                checked={choice === mediaType}
                onSelect={() => onMediaTypeChange(choice)}
                label={t(MEDIA_TYPE_KEYS[choice ?? 'all'])}
                count={mediaTypeCount(choice)}
              />
            ))}
          </div>

          <div className="my-1 border-t border-border" />

          {/* Section « Filtres » : bascule « Non taguées » et, si l'IA est activée,
              « À valider (IA) » (système « Taguer »). Mutuellement exclusives (gérées par
              l'appelant). Case cochée signalée par une COCHE + `aria-checked` (jamais la
              couleur seule). Le filtre « À valider (IA) » est MASQUÉ quand le coupe-circuit
              IA global est désactivé (`aiEnabled === false`) — il n'a aucun sens sans IA. */}
          <p id={filterLabelId} className="px-3 py-1.5 text-xs font-medium text-muted">
            {t('sort.filterSection')}
          </p>
          <div aria-labelledby={filterLabelId}>
            <button
              type="button"
              role="checkbox"
              aria-checked={untaggedOnly}
              onClick={() => onUntaggedOnlyChange(!untaggedOnly)}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand aria-checked:font-semibold aria-checked:text-brand motion-reduce:transition-none"
            >
              <span className="min-w-0 flex-1 truncate">{t('sort.untagged')}</span>
              {untaggedOnly ? (
                <CheckIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
              ) : (
                <span className="h-4 w-4 shrink-0" aria-hidden="true" />
              )}
            </button>
            {aiEnabled && (
              <button
                type="button"
                role="checkbox"
                aria-checked={aiReviewOnly}
                onClick={() => onAiReviewOnlyChange(!aiReviewOnly)}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand aria-checked:font-semibold aria-checked:text-brand motion-reduce:transition-none"
              >
                <span className="min-w-0 flex-1 truncate">{t('sort.aiReview')}</span>
                {aiReviewOnly ? (
                  <CheckIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                ) : (
                  <span className="h-4 w-4 shrink-0" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
